/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Eye, Pencil, Trash2, UserPlus } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { type Client } from '../../lib/api/resources.js';
import { INTAKE_STATUS_TONE } from '../intake/intake-form-utils.js';

import type { IntakeStatus } from '@therapy/shared-validators';

const STATUS_TONE: Readonly<Record<Client['status'], BadgeTone>> = {
  active: 'green',
  inactive: 'slate',
  discharged: 'amber',
};

function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  const result = `${first}${second}`.toUpperCase().slice(0, 2);
  return result === '' ? '?' : result;
}

/** Name sub-line: guardian (Figma), falling back to age when no guardian is set. */
function SubLine({ client }: { readonly client: Client }): JSX.Element | null {
  const { t } = useTranslation();
  const text =
    client.guardianName ??
    (client.age !== null ? t('admin.clients.age', { age: client.age }) : null);
  return text !== null ? (
    <p className="text-xs text-slate-500 dark:text-slate-400">{text}</p>
  ) : null;
}

interface ClientRowProps {
  readonly client: Client;
  readonly intakeStatus?: IntakeStatus | null;
  readonly onView: (client: Client) => void;
  readonly onEdit: (client: Client) => void;
  readonly onDelete: (client: Client) => void;
  readonly onInvite: (client: Client) => void;
}

const ACTION_BTN =
  'focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200';

/** One row of the Admin → Clients table (Figma layout). */
export function ClientTableRow({
  client,
  intakeStatus,
  onView,
  onEdit,
  onDelete,
  onInvite,
}: ClientRowProps): JSX.Element {
  const { t } = useTranslation();
  const view = useCallback(() => {
    onView(client);
  }, [onView, client]);
  const edit = useCallback(() => {
    onEdit(client);
  }, [onEdit, client]);
  const remove = useCallback(() => {
    onDelete(client);
  }, [onDelete, client]);
  const invite = useCallback(() => {
    onInvite(client);
  }, [onInvite, client]);

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
            {initials(client.fullName)}
          </span>
          <div>
            <p className="font-medium text-slate-800 dark:text-white">{client.fullName}</p>
            <SubLine client={client} />
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {client.primaryDiagnosis ?? '—'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {client.therapistName ?? '—'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{client.sessionsCount}</td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="w-24">
            <ProgressBar value={client.progressPct} />
          </div>
          <span className="text-xs text-slate-500 dark:text-slate-400">{client.progressPct}%</span>
        </div>
      </td>
      <td className="px-5 py-3">
        <Badge label={t(`status.${client.status}`)} tone={STATUS_TONE[client.status]} />
      </td>
      <td className="px-5 py-3">
        {intakeStatus === undefined || intakeStatus === null ? (
          <span className="text-slate-400">—</span>
        ) : (
          <Badge
            label={t(`intake.status.${intakeStatus}`)}
            tone={INTAKE_STATUS_TONE[intakeStatus]}
          />
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-1">
          <button type="button" onClick={view} className={ACTION_BTN} aria-label={t('common.view')}>
            <Eye className="h-4 w-4" aria-hidden="true" />
          </button>
          <button type="button" onClick={edit} className={ACTION_BTN} aria-label={t('common.edit')}>
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={invite}
            className={ACTION_BTN}
            aria-label={t('admin.invite.title')}
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={remove}
            className={ACTION_BTN}
            aria-label={t('common.delete')}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
}
